/**
 * Whole-block moves for the block builder: the drag handle, its menu, and Alt+Shift+Arrow.
 *
 * A "movable block" is a direct child of a container whose content is a free list of blocks — a
 * section body, a loop body, or a conditional branch. Everything else moves with its container:
 * a table moves as a table (never a row or cell), a list as a list (never an item), and a loop or
 * conditional with everything inside it. Header/footer parts are section attrs, not doc content,
 * so they are never reached.
 *
 * Every operation is one transaction, so a single undo restores the original position.
 */
import { Extension } from '@tiptap/core'
import { Fragment, Slice, type Node as PMNode } from '@tiptap/pm/model'
import { NodeSelection, Plugin, PluginKey, TextSelection, type EditorState, type Transaction } from '@tiptap/pm/state'
import { dropPoint } from '@tiptap/pm/transform'
import type { EditorView } from '@tiptap/pm/view'
import { defaultParagraphFormat } from './types'

export const MOVABLE_CONTAINERS = new Set(['section', 'loop', 'branch'])

export const BLOCK_DRAG_MIME = 'application/x-docmorphia-block'

export interface MovableBlock {
  pos: number
  node: PMNode
}

/**
 * The innermost movable block at `pos`. `inside` is posAtCoords' `inside` — the node the pointer
 * is over — which is what lets a hover on a loop's padding pick the loop rather than nothing.
 */
export function findMovableBlock(doc: PMNode, pos: number, inside = -1): MovableBlock | null {
  if (inside >= 0 && inside < doc.content.size) {
    const node = doc.nodeAt(inside)
    const $in = doc.resolve(inside)
    if (node?.isBlock && MOVABLE_CONTAINERS.has($in.parent.type.name)) return { pos: inside, node }
    pos = inside
  }
  const $pos = doc.resolve(Math.max(0, Math.min(pos, doc.content.size)))
  if (MOVABLE_CONTAINERS.has($pos.parent.type.name) && $pos.nodeAfter?.isBlock) return { pos: $pos.pos, node: $pos.nodeAfter }
  for (let d = $pos.depth; d >= 1; d--) {
    if (MOVABLE_CONTAINERS.has($pos.node(d - 1).type.name)) return { pos: $pos.before(d), node: $pos.node(d) }
  }
  return null
}

/** The block a keyboard command acts on: a selected block itself, else the one holding the cursor. */
export function blockForSelection(state: EditorState): MovableBlock | null {
  const sel = state.selection
  if (sel instanceof NodeSelection && sel.node.isBlock && MOVABLE_CONTAINERS.has(sel.$from.parent.type.name)) {
    return { pos: sel.from, node: sel.node }
  }
  return findMovableBlock(state.doc, sel.from)
}

function emptyParagraph(state: EditorState): PMNode {
  return state.schema.nodes.paragraph.create({ format: defaultParagraphFormat() })
}

/** Deletes a block, leaving an empty paragraph when it was its container's only child (block+). */
function removeBlock(tr: Transaction, state: EditorState, block: MovableBlock) {
  const $pos = tr.doc.resolve(block.pos)
  const end = block.pos + block.node.nodeSize
  if ($pos.parent.childCount === 1) tr.replaceWith(block.pos, end, emptyParagraph(state))
  else tr.delete(block.pos, end)
}

/** Restores a selection that sat inside a block onto that block's new position. */
function reselect(tr: Transaction, state: EditorState, block: MovableBlock, newPos: number) {
  const sel = state.selection
  if (sel instanceof NodeSelection && sel.from === block.pos) {
    tr.setSelection(NodeSelection.create(tr.doc, newPos))
    return
  }
  const end = block.pos + block.node.nodeSize
  if (sel.from >= block.pos && sel.to <= end) {
    const delta = newPos - block.pos
    tr.setSelection(TextSelection.between(tr.doc.resolve(sel.anchor + delta), tr.doc.resolve(sel.head + delta)))
  }
}

type Dispatch = ((tr: Transaction) => void) | undefined

export function moveBlock(state: EditorState, dispatch: Dispatch, dir: -1 | 1, block = blockForSelection(state)): boolean {
  if (!block) return false
  const $pos = state.doc.resolve(block.pos)
  const index = $pos.index()
  const parent = $pos.parent
  const siblingIndex = index + dir
  if (siblingIndex < 0 || siblingIndex >= parent.childCount) return false
  if (!dispatch) return true
  const sibling = parent.child(siblingIndex)
  const tr = state.tr
  let newPos: number
  if (dir < 0) {
    const start = block.pos - sibling.nodeSize
    tr.replaceWith(start, block.pos + block.node.nodeSize, Fragment.from([block.node, sibling]))
    newPos = start
  } else {
    const end = block.pos + block.node.nodeSize + sibling.nodeSize
    tr.replaceWith(block.pos, end, Fragment.from([sibling, block.node]))
    newPos = block.pos + sibling.nodeSize
  }
  reselect(tr, state, block, newPos)
  dispatch(tr.scrollIntoView())
  return true
}

export function duplicateBlock(state: EditorState, dispatch: Dispatch, block = blockForSelection(state)): boolean {
  if (!block) return false
  if (!dispatch) return true
  const at = block.pos + block.node.nodeSize
  const tr = state.tr.insert(at, block.node.copy(block.node.content))
  tr.setSelection(NodeSelection.create(tr.doc, at))
  dispatch(tr.scrollIntoView())
  return true
}

export function deleteBlock(state: EditorState, dispatch: Dispatch, block = blockForSelection(state)): boolean {
  if (!block) return false
  if (!dispatch) return true
  const tr = state.tr
  removeBlock(tr, state, block)
  const $at = tr.doc.resolve(Math.min(block.pos, tr.doc.content.size))
  tr.setSelection(TextSelection.near($at))
  dispatch(tr.scrollIntoView())
  return true
}

/**
 * Moves `block` to the block boundary nearest `target`. A target inside the block itself (or
 * resolving to its own position) is a no-op, as is a target where the block cannot legally go.
 */
export function moveBlockTo(state: EditorState, dispatch: Dispatch, block: MovableBlock, target: number): boolean {
  const end = block.pos + block.node.nodeSize
  if (state.doc.nodeAt(block.pos) !== block.node) return false
  if (target > block.pos && target < end) return false
  const insertAt = dropPoint(state.doc, target, new Slice(Fragment.from(block.node), 0, 0))
  if (insertAt == null || (insertAt >= block.pos && insertAt <= end)) return false
  if (!dispatch) return true
  const tr = state.tr
  removeBlock(tr, state, block)
  const mapped = tr.mapping.map(insertAt)
  tr.insert(mapped, block.node)
  tr.setSelection(NodeSelection.create(tr.doc, mapped))
  dispatch(tr.scrollIntoView())
  return true
}

// The block a handle drag started on, per view. Cleared on drop/dragend.
const activeDrags = new WeakMap<EditorView, MovableBlock>()

/** Starts a handle drag: selects the block and primes ProseMirror's drag state so the drop cursor shows. */
export function startBlockDrag(view: EditorView, block: MovableBlock, dataTransfer: DataTransfer | null) {
  view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, block.pos)))
  const slice = view.state.selection.content()
  view.dragging = { slice, move: true }
  activeDrags.set(view, block)
  if (!dataTransfer) return
  dataTransfer.clearData()
  const serialized = (view as EditorView & { serializeForClipboard?: (s: Slice) => { dom: HTMLElement; text: string } }).serializeForClipboard?.(slice)
  if (serialized) {
    dataTransfer.setData('text/html', serialized.dom.innerHTML)
    dataTransfer.setData('text/plain', serialized.text)
  }
  dataTransfer.setData(BLOCK_DRAG_MIME, String(block.pos))
  dataTransfer.effectAllowed = 'move'
}

export function endBlockDrag(view: EditorView) {
  activeDrags.delete(view)
  if (view.dragging) view.dragging = null
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    blockMove: {
      moveBlockUp: () => ReturnType
      moveBlockDown: () => ReturnType
      duplicateBlock: () => ReturnType
      deleteBlock: () => ReturnType
    }
  }
}

export const BlockMove = Extension.create({
  name: 'blockMove',
  addCommands() {
    return {
      moveBlockUp: () => ({ state, dispatch }) => moveBlock(state, dispatch, -1),
      moveBlockDown: () => ({ state, dispatch }) => moveBlock(state, dispatch, 1),
      duplicateBlock: () => ({ state, dispatch }) => duplicateBlock(state, dispatch),
      deleteBlock: () => ({ state, dispatch }) => deleteBlock(state, dispatch),
    }
  },
  addKeyboardShortcuts() {
    return {
      'Alt-Shift-ArrowUp': () => this.editor.commands.moveBlockUp(),
      'Alt-Shift-ArrowDown': () => this.editor.commands.moveBlockDown(),
    }
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('blockMoveDrop'),
        props: {
          handleDrop(view, event) {
            const block = activeDrags.get(view)
            if (!block) return false
            activeDrags.delete(view)
            const coords = view.posAtCoords({ left: event.clientX, top: event.clientY })
            // Handled either way: a drop we decline must not fall through to the default
            // handler, which would delete-and-reinsert relative to a stale selection.
            if (coords) moveBlockTo(view.state, view.dispatch, block, coords.pos)
            view.dragging = null
            return true
          },
        },
      }),
    ]
  },
})
